import type {
  AlertMatrixContent,
  LibraryAssetContent,
  QuestionnaireContent,
  SeverityRouting,
} from "@/api/library";
import {
  ALERT_FLAGS,
  CHANNEL_LABELS,
  FLAG_LABELS,
  SEVERITY_LABELS,
  unreadableMatrixSubjectProblem,
} from "@/lib/alertMatrix";
import {
  NO_VALIDITY_SENTENCE,
  itemCountSentence,
  scoringSentence,
  validityStateLabel,
  validityThresholdSentence,
} from "@/lib/questionnaire";
import { Badge } from "@/ui/Badge";
import { ErrorState } from "@/ui/states";

/**
 * One immutable version's content, read-only.
 *
 * This is a viewing surface, not an editor: it renders no input, no save, no
 * add/remove — the authoring surfaces are #33/#34/#35's. Content exists only
 * at an explicit version by contract, which is why this component takes the
 * already-fetched detail rather than an asset id.
 */
export function VersionContent({ content }: { content: LibraryAssetContent }) {
  switch (content.kind) {
    case "parts":
      return (
        <ol className="space-y-4">
          {content.parts.map((part) => (
            <li key={part.key} className="space-y-1.5">
              <p className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs text-foreground">
                  {part.key}
                </span>
                <Badge tone="outline">
                  {part.kind === "prompt_text" ? "Prompt" : "Structured"}
                </Badge>
              </p>
              <pre className="overflow-x-auto whitespace-pre-wrap rounded-md bg-muted px-3 py-2 text-xs text-foreground">
                {part.kind === "prompt_text"
                  ? part.text
                  : JSON.stringify(part.data, null, 2)}
              </pre>
            </li>
          ))}
        </ol>
      );

    case "questionnaire":
      return <QuestionnaireView content={content} />;

    case "alert_matrix": {
      const subjectProblem = unreadableMatrixSubjectProblem(content);
      return subjectProblem ? (
        <ErrorState
          title="This matrix version cannot be read safely"
          description={`${subjectProblem.message} The remaining matrix content is hidden so this version cannot be mistaken for a complete readable record.`}
        />
      ) : (
        <AlertMatrixView content={content} />
      );
    }
  }
}

/**
 * A whole questionnaire, read in full.
 *
 * `validity` was FETCHED and rendered nowhere before #368 — so a clinician
 * deciding whether to adopt an instrument could not see that it carried a
 * malingering screen, which items fed it, or the threshold it fires at. That is
 * the same gap #35 closed on the alert-matrix arm, and it is closed here on the
 * same principle: everything the contract stores is on screen.
 *
 * The scoring model is shown the way it is stored — an ABSENT `scoringMethod`
 * reads as the total, because that is what the service documents an absent
 * field to mean, and an item that is not re-keyed carries no badge.
 *
 * The sentences that describe the instrument rather than list it come from
 * `lib/questionnaire.ts` (#369). They were written here and they still read
 * exactly as they did; what changed is that a Project's adoption row now says
 * the same things about the version it pins, and one instrument described in
 * two vocabularies is a clinician reading two different answers.
 */
function QuestionnaireView({ content }: { content: QuestionnaireContent }) {
  const bands = content.severityBands ?? [];

  return (
    <div className="space-y-4 text-sm">
      <p className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
        <span>Language: {content.language}</span>
        <span>{scoringSentence(content)}</span>
        <span>{itemCountSentence(content)}</span>
      </p>
      {content.instructions ? (
        <p className="italic text-muted-foreground">{content.instructions}</p>
      ) : null}

      <ol className="list-decimal space-y-1 pl-5 text-foreground">
        {content.items.map((item) => (
          <li key={item.id}>
            <span className="flex flex-wrap items-center gap-2">
              {/* The id is stored content, and it is how the validity screen
                  names its indicators. Without it here, "Indicators: q1" cannot
                  be read back to the item it points at — which on a seeded
                  questionnaire is the only surface there is. */}
              <span className="font-mono text-xs text-muted-foreground">
                {item.id}
              </span>
              <span>{item.text}</span>
              {item.reverseScored ? (
                <Badge tone="outline">Reverse scored</Badge>
              ) : null}
            </span>
          </li>
        ))}
      </ol>

      <p className="text-muted-foreground">
        Response scale:{" "}
        {content.responseScale
          .map((option) => `${option.value} — ${option.label}`)
          .join(" · ")}
      </p>

      {bands.length > 0 ? (
        <p className="text-muted-foreground">
          Severity bands:{" "}
          {/* The band's own name and the runtime severity label are separate
              stored fields by contract, so showing only the label leaves the
              authored name unauditable. Both appear; they are often equal, and
              then the name is not repeated. */}
          {bands
            .map((band) =>
              band.name === band.severityLabel
                ? `${band.severityLabel} (${band.scoreFrom}–${band.scoreTo})`
                : `${band.name} → ${band.severityLabel} (${band.scoreFrom}–${band.scoreTo})`,
            )
            .join(" · ")}
        </p>
      ) : null}

      <section className="space-y-1">
        <h3 className="font-medium text-foreground">Validity screen</h3>
        {content.validity ? (
          <>
            <p className="flex flex-wrap items-center gap-2 text-muted-foreground">
              <Badge tone="outline">
                {validityStateLabel(content.validity)}
              </Badge>
              <span>{validityThresholdSentence(content.validity)}</span>
            </p>
            <p className="text-muted-foreground">
              Indicators:{" "}
              <span className="font-mono text-xs text-foreground">
                {content.validity.indicatorItemIds.join(", ")}
              </span>
            </p>
          </>
        ) : (
          <p className="text-muted-foreground">{NO_VALIDITY_SENTENCE}</p>
        )}
      </section>
    </div>
  );
}

/** "Yellow flags: at least 2 → Medium", in words rather than in field names. */
function Threshold({
  condition,
  severity,
  label,
}: {
  condition: string;
  severity: keyof typeof SEVERITY_LABELS;
  label: string | null | undefined;
}) {
  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="text-foreground">
        {condition} → {SEVERITY_LABELS[severity]}
      </span>
      {label ? (
        <span className="text-muted-foreground">— {label}</span>
      ) : null}
    </li>
  );
}

/**
 * One routing entry, with every template it carries.
 *
 * The three template fields are rendered whenever they are set, not only for
 * the channel they belong to: the contract already refuses a template for an
 * unrouted channel, so anything stored here IS live, and hiding it would show
 * a clinician a matrix whose messages they cannot read before adopting it.
 */
function RoutingEntry({ route }: { route: SeverityRouting }) {
  const templates: [string, string | null | undefined][] = [
    ["Email subject", route.emailSubject],
    ["Email body", route.emailBody],
    ["SMS message", route.smsBody],
  ];
  const present = templates.filter(([, value]) => value != null && value !== "");

  return (
    <li className="space-y-1.5 rounded-md border border-border p-3">
      <p className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-foreground">
          {SEVERITY_LABELS[route.severity]}
        </span>
        {route.channels.map((channel) => (
          <Badge key={channel} tone="outline">
            {CHANNEL_LABELS[channel]}
          </Badge>
        ))}
      </p>
      {present.length === 0 ? (
        <p className="text-muted-foreground">
          No message template — this severity dispatches a notification without
          authored text.
        </p>
      ) : (
        <dl className="space-y-1">
          {present.map(([label, value]) => (
            <div key={label} className="flex flex-wrap gap-x-2">
              <dt className="text-muted-foreground">{label}:</dt>
              <dd className="min-w-0 flex-1 whitespace-pre-wrap break-words text-foreground">
                {value}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </li>
  );
}

/**
 * A whole alert matrix, read in full.
 *
 * Everything the contract stores is on screen: every domain's per-flag
 * thresholds, every cross-domain rule's body, every routing entry and all three
 * message templates. Before #35 this arm fetched all of that and rendered only
 * the domain names and a severity-to-channel line, so a clinician could not
 * read the matrix a project was about to adopt.
 *
 * Destination REFERENCES are shown; destination values are not here to show.
 * They live outside the clinical library entirely, and the contract's slug
 * pattern plus #637's structural refusal are what keep an address, a phone
 * number, a URL or a credential out of immutable clinical content.
 *
 * This historical view states exactly what the immutable version stored. The
 * current registry may now mark a ref revoked or unavailable; that does not
 * rewrite history, and project adoption recovery keeps the exact pin removable.
 */
function AlertMatrixView({ content }: { content: AlertMatrixContent }) {
  const combos = content.combos ?? [];

  return (
    <div className="space-y-4 text-sm">
      <p className="text-muted-foreground">
        {content.domains.length}{" "}
        {content.domains.length === 1 ? "domain" : "domains"}
        {" · "}
        {combos.length} cross-domain {combos.length === 1 ? "rule" : "rules"}
        {" · "}
        {content.routing.length} routed{" "}
        {content.routing.length === 1 ? "severity" : "severities"}
      </p>

      <section className="space-y-2">
        <h4 className="font-medium text-foreground">Domains and thresholds</h4>
        <ul className="space-y-2">
          {content.domains.map((domain) => (
            <li key={domain.key} className="rounded-md border border-border p-3">
              <p className="flex flex-wrap items-baseline gap-2">
                <span className="font-medium text-foreground">
                  {domain.displayName}
                </span>
                <span className="font-mono text-xs text-muted-foreground">
                  {domain.key}
                </span>
              </p>
              <ul className="mt-1 space-y-0.5">
                {ALERT_FLAGS.flatMap((flag) => {
                  const rule = domain.rules.find((entry) => entry.flag === flag);
                  return rule
                    ? [
                        <Threshold
                          key={flag}
                          condition={`${FLAG_LABELS[flag]}: at least ${rule.minCount}`}
                          severity={rule.severity}
                          label={rule.label}
                        />,
                      ]
                    : [];
                })}
              </ul>
            </li>
          ))}
        </ul>
      </section>

      <section className="space-y-2">
        <h4 className="font-medium text-foreground">Cross-domain rules</h4>
        {combos.length === 0 ? (
          <p className="text-muted-foreground">
            None — every alert comes from a single domain.
          </p>
        ) : (
          <ul className="space-y-0.5">
            {combos.map((combo) => (
              <Threshold
                key={`${combo.flag}-${combo.minDomains}-${combo.severity}`}
                condition={`${FLAG_LABELS[combo.flag]} in at least ${combo.minDomains} domains`}
                severity={combo.severity}
                label={combo.label}
              />
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-2">
        <h4 className="font-medium text-foreground">Routing and messages</h4>
        <ul className="space-y-2">
          {content.routing.map((route) => (
            <RoutingEntry key={route.severity} route={route} />
          ))}
        </ul>
      </section>

      <section className="space-y-1">
        <h4 className="font-medium text-foreground">Destinations</h4>
        <p className="text-muted-foreground">
          {/* Opaque destination references, never addresses — the values live
              outside the clinical library, not in library content. */}
          <span className="font-mono text-xs text-foreground">
            {content.destinationRefs.join(", ")}
          </span>
        </p>
        <p className="text-xs text-muted-foreground">
          References to configured destinations. The addresses, numbers and URLs
          they resolve to are held outside the clinical library, and this view
          does not resolve them.
        </p>
      </section>
    </div>
  );
}
